import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

const FACILITY_VALIDATE_FETCH_TIMEOUT_MS = 120000;

async function fetchWithTimeout(input: string, init?: RequestInit): Promise<Response> {
  const raw = { ...(init ?? {}) } as Record<string, unknown>;
  const timeoutMs =
    typeof raw.timeoutMs === "number" ? raw.timeoutMs : FACILITY_VALIDATE_FETCH_TIMEOUT_MS;
  delete raw.timeoutMs;
  const rest = raw as RequestInit;
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } catch (e) {
    if (timedOut) {
      throw new Error(
        `Request timed out after ${Math.round(timeoutMs / 1000)}s. Try again or check your network.`,
      );
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Service configuration
const BASE_URL = getApiOrigin();
const FACILITY_TYPES_PATH = "/api/v1/facility-types/countries";
const STATES_PATH = "/api/v1/facility-types/states";

// API Response Types
export interface StateData {
  id: number;
  name: string;
  state_province: string;
  state_abbreviation: string;
}

export interface StateOption {
  value: string;
  label: string;
}

// Main service functions
export async function getCountries(): Promise<string[]> {
  const url = `${BASE_URL}${FACILITY_TYPES_PATH}`;

  const token = getValidAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  check401(response);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      text || `Failed to fetch facility types: ${response.status}`
    );
  }

  return await response.json();
}

export async function getStates(country: string = "United States"): Promise<StateData[]> {
  const url = `${BASE_URL}${STATES_PATH}/${encodeURIComponent(country)}`;

  const token = getValidAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers,
  });

  check401(response);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to fetch states: ${response.status}`);
  }

  return await response.json();
}

// Facility Validation Types
export interface FacilityValidationRequest {
  site_name: string;
  city: string;
  state: string;
  country?: string; // pass country code now
  org_name?: string;
}

export interface FacilitySuggestion {
  site_name: string;
  city: string;
  state: string;
  facility_url?: string;
  similarity_score?: number;
  facility_type?: string;
  facility_type_id?: number;
  // Address fields for displaying facility location
  primary_address?: {
    address_1?: string;
    address_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country_code?: string;
  };
}

export interface FacilityValidationResult {
  success: boolean;
  is_valid?: boolean;
  facilities?: FacilityMatch[];
  suggestions?: FacilitySuggestion[];
  message?: string;
  error?: string;
}
export interface FacilityMatch {
  // id: string;
  site_name: string;
  // address: string;
  city: string;
  state: string;
  // confidence: number;
  selected?: boolean;
  facility_type?: string;
  facility_type_slug?: string;
  facility_type_id?: number;
}
export interface Facility {
  site_name: string;
  city: string;
  state: string;
  county?: string;
  facility_url?: string;
  facility_type?: string;
  facility_type_slug?: string;
  category?: string;
  selected?: boolean;
  facility_type_id?: number;
}
export interface FacilityValidationResponse {
  success: boolean;
  is_valid?: boolean;
  message?: string;
  facility?: Facility; // main facility
  parent_facility?: Facility | null; // optional parent
  sibling_facilities?: Facility[]; // related facilities
  suggestions?: FacilitySuggestion[]; // from your earlier type
  error?: string;
}

function BuildFacilitiesData(
  facilityData: FacilityValidationResponse
): FacilityMatch[] {
  const facilities: Facility[] = [];
  console.log(facilityData);
  // 1. Add parent facility if exists
  if (facilityData.parent_facility) {
    facilities.push({
      ...facilityData.parent_facility,
      // site_name: facilityData.parent_facility.site_name,
      selected: false,
    });
  }

  // 2. Add main facility (selected = true by default)
  if (facilityData.facility) {
    facilities.push({
      ...facilityData.facility,
      // site_name: facilityData.facility.site_name,
      selected: true,
    });
  }

  // 2. Add main facility (selected = true by default)
  if (
    facilityData.sibling_facilities &&
    facilityData.sibling_facilities.length > 0
  ) {
    facilityData.sibling_facilities.forEach((sibling) => {
      facilities.push({
        ...sibling,
        // site_name: sibling.site_name,
        selected: false,
      });
    });
  }
  return facilities;
}

// Facility validation service
export async function validateFacility(
  facilityData: FacilityValidationRequest
): Promise<FacilityValidationResult> {
  const params = new URLSearchParams({
    site_name: facilityData.site_name,
    city: facilityData.city,
    state: facilityData.state,
    ...(facilityData.country && { country: facilityData.country }),
    ...(facilityData.org_name && { org_name: facilityData.org_name }),
  });

  const url = `${BASE_URL}/api/v1/facility-validate?${params.toString()}`;

  const token = getValidAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetchWithTimeout(url, {
    method: "GET",
    headers,
  });

  check401(response);

  if (!response.ok) {
    const text = await response.text();
    return {
      success: false,
      error: text || `Facility validation failed: ${response.status}`,
    };
  }

  const data = await response.json();

  // Handle both success and failure scenarios based on is_valid field
  if (data.is_valid === false) {
    return {
      success: true, // API call succeeded
      is_valid: false,
      suggestions: data.suggestions || [],
      message: "Facility not found",
    };
  }
  console.log("data", data);

  // Transform the response to match our interface for success case
  return {
    success: true,
    is_valid: true,
    // facilities: data.facility || [
    //   {
    //     id: "1",
    //     name: facilityData.site_name,
    //     address: `${facilityData.city}, ${facilityData.state}`,
    //     city: facilityData.city,
    //     state: facilityData.state,
    //     confidence: 0.95,
    //   },
    // ],
    facilities: BuildFacilitiesData(data),
    message: "Facility found successfully",
  };
}
